import ReactDOMServer from "react-dom/server";
import { SvgIconProps } from "@mui/material/SvgIcon";

// MUI Icons
import BugReportRounded from "@mui/icons-material/BugReportRounded";
import NightShelterRoundedIcon from "@mui/icons-material/NightShelterRounded";
import WhatshotRoundedIcon from "@mui/icons-material/WhatshotRounded";
import LandscapeRoundedIcon from "@mui/icons-material/LandscapeRounded";
import GrainRoundedIcon from "@mui/icons-material/GrainRounded";
import AcUnitRoundedIcon from "@mui/icons-material/AcUnitRounded";
import AccessibleRoundedIcon from "@mui/icons-material/AccessibleRounded";
import AddAPhotoRoundedIcon from "@mui/icons-material/AddAPhotoRounded";
import AgricultureRoundedIcon from "@mui/icons-material/AgricultureRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import AssistantDirectionRoundedIcon from "@mui/icons-material/AssistantDirectionRounded";
import AssistantPhotoRoundedIcon from "@mui/icons-material/AssistantPhotoRounded";
import AssistantRoundedIcon from "@mui/icons-material/AssistantRounded";
import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import CreateRoundedIcon from "@mui/icons-material/CreateRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DiningRoundedIcon from "@mui/icons-material/DiningRounded";
import DirectionsCarFilledRoundedIcon from "@mui/icons-material/DirectionsCarFilledRounded";
import DoDisturbAltRoundedIcon from "@mui/icons-material/DoDisturbAltRounded";
import DoNotStepRoundedIcon from "@mui/icons-material/DoNotStepRounded";
import EggRoundedIcon from "@mui/icons-material/EggRounded";
import ElectricBoltRoundedIcon from "@mui/icons-material/ElectricBoltRounded";
import FmdGoodRoundedIcon from "@mui/icons-material/FmdGoodRounded";
import FollowTheSignsRoundedIcon from "@mui/icons-material/FollowTheSignsRounded";
import FormatAlignJustifyRoundedIcon from "@mui/icons-material/FormatAlignJustifyRounded";
import ForestRoundedIcon from "@mui/icons-material/ForestRounded";
import GamepadRoundedIcon from "@mui/icons-material/GamepadRounded";
import GrassRoundedIcon from "@mui/icons-material/GrassRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import GradeRoundedIcon from "@mui/icons-material/GradeRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import LabelImportantRoundedIcon from "@mui/icons-material/LabelImportantRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import LocalFloristRoundedIcon from "@mui/icons-material/LocalFloristRounded";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import NaturePeopleRoundedIcon from "@mui/icons-material/NaturePeopleRounded";
import NatureRoundedIcon from "@mui/icons-material/NatureRounded";
import ParkRoundedIcon from "@mui/icons-material/ParkRounded";
import PedalBikeRoundedIcon from "@mui/icons-material/PedalBikeRounded";
import PetsRoundedIcon from "@mui/icons-material/PetsRounded";
import RadioButtonCheckedRoundedIcon from "@mui/icons-material/RadioButtonCheckedRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TextureRoundedIcon from "@mui/icons-material/TextureRounded";
import ViewAgendaRoundedIcon from "@mui/icons-material/ViewAgendaRounded";
import WindowRoundedIcon from "@mui/icons-material/WindowRounded";
import LocalParkingRoundedIcon from "@mui/icons-material/LocalParkingRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import OtherHousesRoundedIcon from "@mui/icons-material/OtherHousesRounded";
import StairsRoundedIcon from "@mui/icons-material/StairsRounded";
import WaterDamageRoundedIcon from "@mui/icons-material/WaterDamageRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import AlbumRoundedIcon from "@mui/icons-material/AlbumRounded";
import SportsCricketRoundedIcon from "@mui/icons-material/SportsCricketRounded";
import BrokenImageRoundedIcon from "@mui/icons-material/BrokenImageRounded";
import SensorDoorRoundedIcon from "@mui/icons-material/SensorDoorRounded";
import Co2RoundedIcon from "@mui/icons-material/Co2Rounded";
import EmojiNatureRoundedIcon from "@mui/icons-material/EmojiNatureRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackwardRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";

// Project import
import { IconID } from "types/auth";
import { SvgIconStyleObject } from "types/mapData";

// Get icon mui component from icon id
export const getIconComponentById = (
  iconId: IconID,
  dimension: number,
  svgStyle?: SvgIconStyleObject
): JSX.Element | null => {
  const Component = iconComponentMap[iconId];
  if (!Component) return null;

  const style: SvgIconProps["sx"] = {
    height: dimension,
    width: dimension,
    backgroundColor: "white",
    color: "secondary.600",
    fontSize: dimension,
  };

  return (
    <Component
      sx={style}
      fill={svgStyle?.fillColor}
      stroke={svgStyle?.strokeColor}
      strokeWidth={svgStyle?.strokeWidth ?? 0}
      htmlColor="black"
    />
  );
};

// Get icon html string from icon id (used in leaflet)
export function getLeafletIconHtml(iconId: IconID, svgStyle?: SvgIconStyleObject): string | null {
  const iconComp = getIconComponentById(iconId, 24, svgStyle);
  if (!iconComp) return null;
  // Render the icon component to a string
  return ReactDOMServer.renderToString(iconComp);
}

// Get icon ligature string from icon id (used in legend for pdf print)
export function getLigatureFromIconId(iconId: IconID): string | null {
  return iconIdToLigature[iconId] || null;
}

// Map of icon IDs to their respective components
export const iconComponentMap: Record<IconID, React.ElementType> = {
  BugReportRounded: BugReportRounded,
  NightShelterRoundedIcon: NightShelterRoundedIcon,
  LandscapeRoundedIcon: LandscapeRoundedIcon,
  GrainRoundedIcon,
  AcUnitRoundedIcon,
  AccessibleRoundedIcon,
  AddAPhotoRoundedIcon,
  AgricultureRoundedIcon,
  AssignmentRoundedIcon,
  AssistantDirectionRoundedIcon,
  AssistantPhotoRoundedIcon,
  AssistantRoundedIcon,
  ConstructionRoundedIcon,
  CreateRoundedIcon,
  DeleteRoundedIcon,
  DiningRoundedIcon,
  DirectionsCarFilledRoundedIcon,
  DoDisturbAltRoundedIcon,
  DoNotStepRoundedIcon,
  EggRoundedIcon,
  ElectricBoltRoundedIcon,
  FmdGoodRoundedIcon,
  FollowTheSignsRoundedIcon,
  FormatAlignJustifyRoundedIcon,
  ForestRoundedIcon,
  GamepadRoundedIcon,
  GrassRoundedIcon,
  GroupRoundedIcon,
  GradeRoundedIcon,
  HelpRoundedIcon,
  LabelImportantRoundedIcon,
  LightModeRoundedIcon,
  LocalFloristRoundedIcon,
  MyLocationRoundedIcon,
  NaturePeopleRoundedIcon,
  NatureRoundedIcon,
  ParkRoundedIcon,
  PedalBikeRoundedIcon,
  WhatshotRoundedIcon,
  PetsRoundedIcon,
  RadioButtonCheckedRoundedIcon,
  ReportProblemRoundedIcon,
  ScienceRoundedIcon,
  SearchRoundedIcon,
  TextureRoundedIcon,
  ViewAgendaRoundedIcon,
  WindowRoundedIcon,
  LocalParkingRoundedIcon,
  GroupsRoundedIcon,
  LocalShippingRoundedIcon,
  VideocamRoundedIcon,
  OtherHousesRoundedIcon,
  StairsRoundedIcon,
  WaterDamageRoundedIcon,
  StopCircleRoundedIcon,
  AlbumRoundedIcon,
  SportsCricketRoundedIcon,
  BrokenImageRoundedIcon,
  SensorDoorRoundedIcon,
  Co2RoundedIcon,
  EmojiNatureRoundedIcon,
  LockRoundedIcon,
  WorkspacesRoundedIcon,
  CollectionsRoundedIcon,
  EventSeatRoundedIcon,
  ArrowUpwardRoundedIcon,
  ArrowDownwardRoundedIcon,
  ArrowForwardRoundedIcon,
  ArrowBackwardRoundedIcon,
  MoreHorizRoundedIcon,
  DragIndicatorRoundedIcon
};

export const POINT_OPTIONS = [
  // Start:
  { id: "FmdGoodRoundedIcon", label: "icon-markers-point" },
  { id: "Text", label: "icon-people-text" },

  // Arrows:
  { id: "ArrowUpwardRoundedIcon", label: "icon-arrow-1" },
  { id: "ArrowDownwardRoundedIcon", label: "icon-arrow-2" },
  { id: "ArrowBackwardRoundedIcon", label: "icon-arrow-3" },
  { id: "ArrowForwardRoundedIcon", label: "icon-arrow-4" },

  // Buildings:
  { id: "OtherHousesRoundedIcon", label: "icon-buildings-1" },
  { id: "WaterDamageRoundedIcon", label: "icon-buildings-2" },
  { id: "NightShelterRoundedIcon", label: "icon-buildings-3" },

  // Miscellaneous:
  { id: "ViewAgendaRoundedIcon", label: "icon-miscellaneous-1" },
  { id: "FormatAlignJustifyRoundedIcon", label: "icon-miscellaneous-2" },
  { id: "TextureRoundedIcon", label: "icon-miscellaneous-3" },
  { id: "WindowRoundedIcon", label: "icon-miscellaneous-4" },
  { id: "MoreHorizRoundedIcon", label: "icon-miscellaneous-5" },
  { id: "WorkspacesRoundedIcon", label: "icon-miscellaneous-6" },
  { id: "DragIndicatorRoundedIcon", label: "icon-miscellaneous-7" },
  { id: "StopCircleRoundedIcon", label: "icon-miscellaneous-8" },
  { id: "AlbumRoundedIcon", label: "icon-miscellaneous-9" },
  { id: "BrokenImageRoundedIcon", label: "icon-miscellaneous-10" },
  { id: "SensorDoorRoundedIcon", label: "icon-miscellaneous-11" },
  { id: "RadioButtonCheckedRoundedIcon", label: "icon-miscellaneous-12" },
  { id: "GamepadRoundedIcon", label: "icon-miscellaneous-13" },

  // Markers:
  { id: "AssistantPhotoRoundedIcon", label: "icon-markers-1" },
  { id: "AssistantRoundedIcon", label: "icon-markers-2" },
  { id: "ReportProblemRoundedIcon", label: "icon-markers-3" },
  { id: "DoDisturbAltRoundedIcon", label: "icon-markers-4" },
  { id: "AssistantDirectionRoundedIcon", label: "icon-markers-5" },
  { id: "FollowTheSignsRoundedIcon", label: "icon-markers-6" },
  { id: "DoNotStepRoundedIcon", label: "icon-markers-7" },
  { id: "AccessibleRoundedIcon", label: "icon-markers-8" },
  { id: "LocalParkingRoundedIcon", label: "icon-markers-9" },
  { id: "Co2RoundedIcon", label: "icon-markers-10" },
  { id: "LabelImportantRoundedIcon", label: "icon-markers-11" },

  // People:
  { id: "GroupRoundedIcon", label: "icon-people-1" },
  { id: "GroupsRoundedIcon", label: "icon-people-2" },
  { id: "NaturePeopleRoundedIcon", label: "icon-people-3" },

  // Nature:
  { id: "LocalFloristRoundedIcon", label: "icon-nature-1" },
  { id: "GrassRoundedIcon", label: "icon-nature-2" },
  { id: "LandscapeRoundedIcon", label: "icon-nature-3" },
  { id: "GrainRoundedIcon", label: "icon-nature-4" },
  { id: "ForestRoundedIcon", label: "icon-nature-5" },
  { id: "NatureRoundedIcon", label: "icon-nature-6" },
  { id: "ParkRoundedIcon", label: "icon-nature-7" },
  { id: "EmojiNatureRoundedIcon", label: "icon-nature-8" },
  { id: "BugReportRounded", label: "icon-nature-9" },
  { id: "PetsRoundedIcon", label: "icon-nature-10" },
  { id: "WhatshotRoundedIcon", label: "icon-nature-11" },
  { id: "EggRoundedIcon", label: "icon-nature-12" },
  { id: "AcUnitRoundedIcon", label: "icon-nature-13" },
  { id: "LightModeRoundedIcon", label: "icon-nature-14" },
  { id: "ElectricBoltRoundedIcon", label: "icon-nature-15" },

  // Objects:
  { id: "AddAPhotoRoundedIcon", label: "icon-objects-1" },
  { id: "VideocamRoundedIcon", label: "icon-objects-2" },
  { id: "CollectionsRoundedIcon", label: "icon-objects-3" },
  { id: "ScienceRoundedIcon", label: "icon-objects-4" },
  { id: "AssignmentRoundedIcon", label: "icon-objects-5" },
  { id: "CreateRoundedIcon", label: "icon-objects-6" },
  { id: "MyLocationRoundedIcon", label: "icon-objects-7" },
  { id: "DeleteRoundedIcon", label: "icon-objects-8" },
  { id: "DiningRoundedIcon", label: "icon-objects-9" },
  { id: "HelpRoundedIcon", label: "icon-objects-10" },
  { id: "GradeRoundedIcon", label: "icon-objects-11" },
  { id: "SearchRoundedIcon", label: "icon-objects-12" },
  { id: "StairsRoundedIcon", label: "icon-objects-13" },
  { id: "SportsCricketRoundedIcon", label: "icon-objects-14" },
  { id: "ConstructionRoundedIcon", label: "icon-objects-15" },
  { id: "LockRoundedIcon", label: "icon-objects-16" },
  { id: "EventSeatRoundedIcon", label: "icon-objects-17" },

  // Transport:
  { id: "DirectionsCarFilledRoundedIcon", label: "icon-transport-1" },
  { id: "LocalShippingRoundedIcon", label: "icon-transport-2" },
  { id: "AgricultureRoundedIcon", label: "icon-transport-3" },
  { id: "PedalBikeRoundedIcon", label: "icon-transport-4" },
];

const iconIdToLigature: Record<IconID, string> = {
  BugReportRounded: "bug_report",
  NightShelterRoundedIcon: "night_shelter",
  LandscapeRoundedIcon: "landscape",
  GrainRoundedIcon: "grain",
  AcUnitRoundedIcon: "ac_unit",
  AccessibleRoundedIcon: "accessible",
  AddAPhotoRoundedIcon: "add_a_photo",
  AgricultureRoundedIcon: "agriculture",
  AssignmentRoundedIcon: "assignment",
  AssistantDirectionRoundedIcon: "assistant_direction",
  AssistantPhotoRoundedIcon: "assistant_photo",
  AssistantRoundedIcon: "assistant",
  ConstructionRoundedIcon: "construction",
  CreateRoundedIcon: "create",
  DeleteRoundedIcon: "delete",
  DiningRoundedIcon: "dining",
  DirectionsCarFilledRoundedIcon: "directions_car",
  DoDisturbAltRoundedIcon: "do_not_disturb_alt",
  DoNotStepRoundedIcon: "do_not_step",
  EggRoundedIcon: "egg",
  ElectricBoltRoundedIcon: "electric_bolt",
  FmdGoodRoundedIcon: "fmd_good",
  FollowTheSignsRoundedIcon: "follow_the_signs",
  FormatAlignJustifyRoundedIcon: "format_align_justify",
  ForestRoundedIcon: "forest",
  GamepadRoundedIcon: "gamepad",
  GrassRoundedIcon: "grass",
  GroupRoundedIcon: "group",
  GradeRoundedIcon: "grade",
  HelpRoundedIcon: "help",
  LabelImportantRoundedIcon: "label_important",
  LightModeRoundedIcon: "light_mode",
  LocalFloristRoundedIcon: "local_florist",
  MyLocationRoundedIcon: "my_location",
  NaturePeopleRoundedIcon: "nature_people",
  NatureRoundedIcon: "nature",
  ParkRoundedIcon: "park",
  PedalBikeRoundedIcon: "pedal_bike",
  WhatshotRoundedIcon: "whatshot",
  PetsRoundedIcon: "pets",
  RadioButtonCheckedRoundedIcon: "radio_button_checked",
  ReportProblemRoundedIcon: "report_problem",
  ScienceRoundedIcon: "science",
  SearchRoundedIcon: "search",
  TextureRoundedIcon: "texture",
  ViewAgendaRoundedIcon: "view_agenda",
  WindowRoundedIcon: "window",
  LocalParkingRoundedIcon: "local_parking",
  GroupsRoundedIcon: "groups",
  LocalShippingRoundedIcon: "local_shipping",
  VideocamRoundedIcon: "videocam",
  OtherHousesRoundedIcon: "other_houses",
  StairsRoundedIcon: "stairs",
  WaterDamageRoundedIcon: "water_damage",
  StopCircleRoundedIcon: "stop_circle",
  AlbumRoundedIcon: "album",
  SportsCricketRoundedIcon: "sports_cricket",
  BrokenImageRoundedIcon: "broken_image",
  SensorDoorRoundedIcon: "sensor_door",
  Co2RoundedIcon: "co2",
  EmojiNatureRoundedIcon: "emoji_nature",
  LockRoundedIcon: "lock",
  WorkspacesRoundedIcon: "workspaces",
  CollectionsRoundedIcon: "collections",
  EventSeatRoundedIcon: "event_seat",
  ArrowUpwardRoundedIcon: "arrow_upward",
  ArrowDownwardRoundedIcon: "arrow_downward",
  ArrowForwardRoundedIcon: "arrow_forward",
  ArrowBackwardRoundedIcon: "arrow_back",
  MoreHorizRoundedIcon: "more_horiz",
  DragIndicatorRoundedIcon: "drag_indicator"
};
